// Le code de sauvegarde : l'état d'une partie en 8 caractères de base 32 de Crockford plus un caractère
// de contrôle, à recopier sur la feuille d'aventure (Safari efface les données d'un site après 7 jours
// sans visite, porte 3). Aucune dépendance : repris par l'appli compagnon.
//
// 40 bits, du plus fort au plus faible : version (4), empreinte du livre (4), rang de la scène (8),
// indices trouvés (12, un bit par indice déclaré), jauges (3 fois 4 bits, dans l'ordre déclaré).

import { ALPHABET, empreinte, normaliserCode } from './codes.js';
import type { Etat, Livre } from './types.js';

const VERSION = 1n;
export const LIMITES = { scenes: 256, indices: 12, jauges: 3, maxJauge: 15 } as const;

export type RefusSauvegarde = 'longueur' | 'caractere' | 'controle' | 'version' | 'autre-livre' | 'hors-limites';

/**
 * Empreinte du livre sur 4 bits : l'identifiant et l'ordre des scènes, des indices et des jauges, dont
 * dépend la lecture des bits. Un code d'un autre livre, ou d'une version réordonnée, est refusé 15 fois
 * sur 16 au lieu d'être mal lu sans bruit.
 */
export function empreinteLivre(livre: Livre): bigint {
  const structure = [livre.id, livre.scenes.map((s) => s.id), livre.indices.map((i) => i.id), livre.jauges.map((j) => j.id)];
  return BigInt(empreinte(JSON.stringify(structure)) & 0xf);
}

/** Produit dans le corps à 32 éléments, polynôme x^5 + x^2 + 1. */
function produitGF32(a: number, b: number): number {
  let resultat = 0;
  let x = a;
  let y = b;
  while (y > 0) {
    if (y & 1) resultat ^= x;
    y >>= 1;
    x <<= 1;
    if (x & 0x20) x ^= 0x25;
  }
  return resultat;
}

/**
 * Algorithme de Damm sur le corps à 32 éléments, avec l'opération x * y = 2x + y, totalement
 * antisymétrique : toute faute sur un seul caractère et tout échange de deux caractères voisins, le
 * caractère de contrôle compris, laissent un reste non nul.
 */
function reste(valeurs: number[]): number {
  return valeurs.reduce((interim, valeur) => produitGF32(2, interim) ^ valeur, 0);
}

function controle(valeurs: number[]): string {
  return ALPHABET[produitGF32(2, reste(valeurs))] as string;
}

/** Écrit un nombre de 40 bits en `XXXX-XXXX-C`, caractère de contrôle compris. */
export function formater(nombre: bigint): string {
  const valeurs: number[] = [];
  for (let i = 7; i >= 0; i--) valeurs.push(Number((nombre >> BigInt(5 * i)) & 31n));
  const caracteres = valeurs.map((valeur) => ALPHABET[valeur]).join('');
  return `${caracteres.slice(0, 4)}-${caracteres.slice(4, 8)}-${controle(valeurs)}`;
}

export function encoder(livre: Livre, etat: Etat): string {
  if (livre.scenes.length > LIMITES.scenes || livre.indices.length > LIMITES.indices || livre.jauges.length > LIMITES.jauges) {
    throw new Error('livre hors des limites du code de sauvegarde');
  }
  const rang = livre.scenes.findIndex((scene) => scene.id === etat.scene);
  if (rang < 0) throw new Error(`scène inconnue : ${etat.scene}`);
  let masque = 0n;
  livre.indices.forEach((indice, i) => {
    if (etat.indices.includes(indice.id)) masque |= 1n << BigInt(i);
  });
  let jauges = 0n;
  livre.jauges.forEach((jauge, i) => {
    const valeur = etat.jauges[jauge.id] ?? 0;
    if (valeur < 0 || valeur > Math.min(jauge.max, LIMITES.maxJauge)) throw new Error(`jauge hors limites : ${jauge.id}`);
    jauges |= BigInt(valeur) << BigInt(4 * (2 - i));
  });
  return formater((VERSION << 36n) | (empreinteLivre(livre) << 32n) | (BigInt(rang) << 24n) | (masque << 12n) | jauges);
}

export function decoder(livre: Livre, saisie: string): { ok: true; etat: Etat } | { ok: false; refus: RefusSauvegarde } {
  const code = normaliserCode(saisie);
  if (code.length !== 9) return { ok: false, refus: 'longueur' };
  const valeurs: number[] = [];
  for (const caractere of code.slice(0, 8)) {
    const valeur = ALPHABET.indexOf(caractere);
    if (valeur < 0) return { ok: false, refus: 'caractere' };
    valeurs.push(valeur);
  }
  const valeurControle = ALPHABET.indexOf(code[8] as string);
  if (valeurControle < 0) return { ok: false, refus: 'caractere' };
  if (reste([...valeurs, valeurControle]) !== 0) return { ok: false, refus: 'controle' };
  let nombre = 0n;
  for (const valeur of valeurs) nombre = (nombre << 5n) | BigInt(valeur);
  if (nombre >> 36n !== VERSION) return { ok: false, refus: 'version' };
  if (((nombre >> 32n) & 0xfn) !== empreinteLivre(livre)) return { ok: false, refus: 'autre-livre' };
  const rang = Number((nombre >> 24n) & 0xffn);
  const masque = Number((nombre >> 12n) & 0xfffn);
  const scene = livre.scenes[rang];
  if (!scene) return { ok: false, refus: 'hors-limites' };
  if (masque >> livre.indices.length !== 0) return { ok: false, refus: 'hors-limites' };
  const jauges: Record<string, number> = {};
  for (let i = 0; i < 3; i++) {
    const valeur = Number((nombre >> BigInt(4 * (2 - i))) & 0xfn);
    const jauge = livre.jauges[i];
    if (!jauge) {
      if (valeur !== 0) return { ok: false, refus: 'hors-limites' };
      continue;
    }
    if (valeur > jauge.max) return { ok: false, refus: 'hors-limites' };
    jauges[jauge.id] = valeur;
  }
  const indices = livre.indices.filter((_, i) => (masque >> i) & 1).map((indice) => indice.id).sort();
  return { ok: true, etat: { scene: scene.id, indices, jauges } };
}
